import React, { useState, useEffect } from 'react';
import { useQueryClient, useIsFetching } from '@tanstack/react-query';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useStories } from '../hooks/useStories.ts';
import { useSidebarStories } from '../hooks/useSidebarStories.ts';
import { useStoryStatus } from '../hooks/useStoryStatus.ts';
import ProgressStepper from './ProgressStepper.tsx';
import Sidebar from './Sidebar.tsx';
import { StoryProvider } from '../contexts/StoryContext.tsx';

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const location = useLocation();
  const { storyId: paramStoryId } = useParams();
  const navigate = useNavigate();
  
  const { stories: availableStories, loading: storiesLoading, refetch: refetchStories } = useStories();
  const { data: sidebarStories = [], isLoading: sidebarLoading, isFetching: sidebarFetching, refetch: refetchSidebarStories } = useSidebarStories();
  const [selectedStoryId, setSelectedStoryId] = useState<number | null>(null);
  const [isSwitchingStory, setIsSwitchingStory] = useState(false);
  const [switchingToStoryId, setSwitchingToStoryId] = useState<number | null>(null);
  const queryClient = useQueryClient();
  const storyDataFetching = useIsFetching({
    predicate: (q) => {
      try {
        const key = q.queryKey as unknown as (string | number | undefined)[];
        const key0 = key?.[0];
        const key1 = key?.[1];
        return (
          (key0 === 'scene-images' || key0 === 'video-status' || key0 === 'final-status') &&
          key1 === selectedStoryId
        );
      } catch {
        return false;
      }
    },
  });

  // Determine current step from pathname
  const getCurrentStep = (): 'characters' | 'scenes' | 'videos' | 'final' | null => {
    const path = location.pathname;
    console.log('AppLayout: getCurrentStep - path:', path);
    if (path.includes('/characters') || path.includes('/character-builder')) return 'characters';
    if (path.includes('/scenes') || path.includes('/scene-builder')) return 'scenes';
    if (path.includes('/videos') || path.includes('/video-builder')) return 'videos';
    if (path.includes('/final-story')) return 'final';
    return null;
  };

  const pathCurrentStep = getCurrentStep();

  // Ensure selectedStoryId is set when stories are available but no story is selected
  useEffect(() => {
    if (availableStories.length > 0 && !selectedStoryId && !storiesLoading) {
      // Use the first story (latest) as the default selection
      const latestStory = availableStories[0];
      console.log('AppLayout Debug - Auto-selecting latest story from useEffect:', latestStory.id);
      setSelectedStoryId(latestStory.id);
    }
  }, [availableStories, storiesLoading, selectedStoryId]);

  // Update selected story when URL changes - selectedStoryId is the single source of truth
  useEffect(() => {
    if (paramStoryId) {
      // Only set from URL if the story actually exists in available stories
      const storyExists = availableStories.some(story => story.id === parseInt(paramStoryId));
      if (storyExists) {
        setSelectedStoryId(parseInt(paramStoryId));
      } else {
        // If story doesn't exist, clear the selection
        setSelectedStoryId(null);
      }
    }
  }, [paramStoryId, availableStories]);

  // Refresh stories list when current story status changes (linked to progress bar polling)
  const { status: currentStoryStatus, loading: storyStatusLoading, error: storyStatusError } = useStoryStatus(selectedStoryId);
  
  // Do not refresh dropdown on intermediate status changes; sidebar will refresh
  // after creation and once final_url is available.

  // Explicitly refresh sidebar once the final URL appears (completed render)
  const [lastFinalUrl, setLastFinalUrl] = useState<string | null>(null);
  useEffect(() => {
    const finalUrl = currentStoryStatus?.final_url || null;
    if (finalUrl && finalUrl !== lastFinalUrl) {
      setLastFinalUrl(finalUrl);
      refetchSidebarStories();
    }
  }, [currentStoryStatus?.final_url, lastFinalUrl, refetchSidebarStories]);

  // Hide spinner when new story's data has loaded (not just when ID matches)
  useEffect(() => {
    const idMatched = isSwitchingStory && switchingToStoryId && selectedStoryId === switchingToStoryId;
    const queriesSettled = storyDataFetching === 0 && !storyStatusLoading;
    if (idMatched && queriesSettled) {
      setIsSwitchingStory(false);
      setSwitchingToStoryId(null);
    }
  }, [isSwitchingStory, switchingToStoryId, selectedStoryId, storyDataFetching, storyStatusLoading]);

  // Debug logging
  console.log('AppLayout Debug:');
  console.log('  selectedStoryId:', selectedStoryId);
  console.log('  availableStoriesCount:', availableStories.length);
  console.log('  availableStoryIds:', availableStories.map(s => s.id));
  console.log('  availableStories data:', availableStories);
  console.log('  paramStoryId:', paramStoryId);
  console.log('  pathCurrentStep:', pathCurrentStep);

  // Only hide sidebar and progress bar on story-setup and home pages
  if (location.pathname === '/story-setup' || location.pathname === '/home') {
    return <>{children}</>;
  }

  return (
    <StoryProvider 
      selectedStoryId={selectedStoryId} 
      setSelectedStoryId={setSelectedStoryId}
      storyStatus={currentStoryStatus}
      storyStatusLoading={storyStatusLoading}
      storyStatusError={storyStatusError}
      isSwitchingStory={isSwitchingStory}
    >
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100">
        {/* Sidebar */}
        <Sidebar
          stories={sidebarStories}
          currentStoryId={selectedStoryId?.toString()}
          loading={sidebarLoading}
          onStoryChange={(newStoryId) => {
            const newStoryIdNum = parseInt(newStoryId);
            setIsSwitchingStory(true);
            setSwitchingToStoryId(newStoryIdNum);

            // Clear cached queries for previous story to avoid stale flashes
            if (selectedStoryId) {
              queryClient.removeQueries({ queryKey: ['scene-images', selectedStoryId], exact: true });
              queryClient.removeQueries({ queryKey: ['video-status', selectedStoryId], exact: true });
              queryClient.removeQueries({ queryKey: ['final-status', selectedStoryId], exact: true });
            }

            // Select and proactively invalidate new story queries
            setSelectedStoryId(newStoryIdNum);
            queryClient.invalidateQueries({ queryKey: ['scene-images', newStoryIdNum] });
            queryClient.invalidateQueries({ queryKey: ['video-status', newStoryIdNum] });
            queryClient.invalidateQueries({ queryKey: ['final-status', newStoryIdNum] });
            refetchSidebarStories();

            // Navigate to the canonical entry page for a story
            navigate(`/character-builder/${newStoryId}`);
          }}
          onStoryDelete={async () => {
            // Refresh both stories lists after deletion
            await Promise.all([
              refetchStories(),
              refetchSidebarStories()
            ]);
            
            // Auto-select the newest story after deletion
            setTimeout(() => {
              // Use the current availableStories state after refresh
              if (availableStories.length > 1) {
                // Find the newest story (excluding the deleted one)
                const remainingStories = availableStories.filter(story => story.id !== selectedStoryId);
                if (remainingStories.length > 0) {
                  const newestStory = remainingStories[0]; // Stories are already sorted by newest first
                  setSelectedStoryId(newestStory.id);
                  navigate(`/character-builder/${newestStory.id}`);
                }
              } else if (availableStories.length === 1) {
                // Only one story left, select it
                const lastStory = availableStories[0];
                setSelectedStoryId(lastStory.id);
                navigate(`/character-builder/${lastStory.id}`);
              } else {
                // No stories left, go to home
                setSelectedStoryId(null);
                navigate('/home');
              }
            }, 200); // Small delay to ensure state updates
          }}
        />
        
        {/* Main Content */}
        <div className="lg:ml-64 transition-all duration-300">
          <div className="container mx-auto px-4 py-8">
            <div className="max-w-6xl mx-auto">
              {/* Persistent Progress Stepper */}
              <ProgressStepper
                currentStep={pathCurrentStep || 'characters'}
                storyId={selectedStoryId?.toString()}
                stories={availableStories}
                onStoryChange={(newStoryId) => {
                  const newStoryIdNum = parseInt(newStoryId);
                  setIsSwitchingStory(true);
                  setSwitchingToStoryId(newStoryIdNum);

                  if (selectedStoryId) {
                    queryClient.removeQueries({ queryKey: ['scene-images', selectedStoryId], exact: true });
                    queryClient.removeQueries({ queryKey: ['video-status', selectedStoryId], exact: true });
                    queryClient.removeQueries({ queryKey: ['final-status', selectedStoryId], exact: true });
                  }

                  setSelectedStoryId(newStoryIdNum);
                  queryClient.invalidateQueries({ queryKey: ['scene-images', newStoryIdNum] });
                  queryClient.invalidateQueries({ queryKey: ['video-status', newStoryIdNum] });
                  queryClient.invalidateQueries({ queryKey: ['final-status', newStoryIdNum] });
                  refetchSidebarStories();
                  navigate(`/character-builder/${newStoryId}`);
                }}
              />
              
              {/* Page Content */}
              {isSwitchingStory ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" aria-live="polite">
                  {Array.from({ length: 2 }).map((_, i) => (
                    <div key={`skeleton-${i}`} className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                      <div className="aspect-video bg-gray-100 animate-pulse" />
                      <div className="p-4">
                        <div className="h-4 w-24 bg-gray-200 rounded mb-3 animate-pulse"></div>
                        <div className="h-2 w-full bg-gray-200 rounded mb-2 animate-pulse"></div>
                        <div className="h-2 w-5/6 bg-gray-200 rounded mb-2 animate-pulse"></div>
                        <div className="h-2 w-2/3 bg-gray-200 rounded animate-pulse"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                children
              )}
            </div>
          </div>
        </div>
        
        {/* Story Switching Loading Overlay */}
        {isSwitchingStory && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Switching Story</h3>
              <p className="text-gray-600">Loading your selected story...</p>
            </div>
          </div>
        )}
      </div>
    </StoryProvider>
  );
};

export default AppLayout;